import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { UserPlus, CheckCircle } from 'lucide-react';
import { Container } from '../../components/styled/Container';
import { PageLayout } from '../../components/styled/PageLayout';
import { regionFromPathname } from '../../utils/regionFromPath';
import { pathWithRegion } from '../../utils/navBase';
import { useDispatch, useSelector } from 'react-redux';
import { clearAuthError, registerUser } from '../../store/reducers/authReducer';
import { useI18n } from '../../hooks/useI18n';
import SEO from '../../components/seo/SEO';

const AuthWrap = styled.div`
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
`;

const AuthCard = styled(motion.div)`
  width: 100%;
  max-width: 26rem;
  padding: 2.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.card};
  box-shadow: ${({ theme }) => theme.shadowCard};
`;

const LogoLink = styled(Link)`
  display: inline-block;
  font-size: 1.6rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 1.5rem;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 0.25rem 0;
  color: ${({ theme }) => theme.colors.foreground};
`;

const Subtitle = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0 0 1.5rem 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.foreground};
  display: block;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  height: 3rem;
  padding: 0 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.muted};
  font-size: 0.875rem;
  outline: none;
  border: 2px solid transparent;
  box-sizing: border-box;
  &:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }
`;

const SubmitBtn = styled.button`
  height: 3rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.crimson};
  color: ${({ theme }) => theme.colors.crimsonForeground};
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: none;
  cursor: pointer;
  &:hover {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Divider = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 1.25rem 0 0 0;
  text-align: center;
`;

const AuthLink = styled(Link)`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  display: block;
  text-align: center;
  margin-top: 0.5rem;
  &:hover {
    text-decoration: underline;
  }
`;

const SuccessMsg = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0.5rem 0 0 0;
`;

export default function RegisterPage() {
  const location = useLocation();
  const region = regionFromPathname(location.pathname);
  const pre = region || '';
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { error } = useSelector((s) => s.auth);
  const { t } = useI18n();

  const homeTo = pre ? `/${pre}` : '/';

  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [localError, setLocalError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setLocalError('');
    dispatch(clearAuthError());
    dispatch(registerUser({ username, displayName: name, email, password }))
      .unwrap()
      .then(() => {
        setSubmitted(true);
        setTimeout(() => navigate(homeTo), 900);
      })
      .catch((err) => {
        setLocalError(err?.message || 'Register failed');
      });
  };

  return (
    <PageLayout>
      <SEO
        region={region}
        path="/register"
        title={t('auth.register.title', 'Create account')}
        description={t('auth.register.subtitle', 'Sign up to save favorites and contribute reviews.')}
        noindex
      />
      <AuthWrap>
        <Container style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <AuthCard initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
            <LogoLink to={pathWithRegion('/', region)}>FliqMatch</LogoLink>
            <Title>{t('auth.register.title', 'Create account')}</Title>
            <Subtitle>{t('auth.register.subtitle', 'Sign up to save favorites and contribute reviews.')}</Subtitle>

            {submitted ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <CheckCircle size={40} style={{ color: 'hsl(142, 71%, 45%)' }} />
                </div>
                <p style={{ margin: '1rem 0 0 0', fontWeight: 600, textAlign: 'center' }}>Account created!</p>
                <SuccessMsg style={{ textAlign: 'center' }}>Redirecting...</SuccessMsg>
              </>
            ) : (
              <>
                <Form onSubmit={handleSubmit}>
                  <div>
                    <Label>Username</Label>
                    <Input
                      type="text"
                      required
                      value={username}
                      onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                      placeholder="your_handle"
                      autoComplete="username"
                      minLength={3}
                      maxLength={30}
                    />
                  </div>
                  <div>
                    <Label>Name</Label>
                    <Input type="text" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                    />
                  </div>
                  <div>
                    <Label>Password</Label>
                    <Input
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      minLength={6}
                    />
                  </div>
                  <SubmitBtn type="submit">
                    <UserPlus size={18} /> Sign up
                  </SubmitBtn>
                  {localError || error ? (
                    <SuccessMsg style={{ color: '#ffb4b4' }}>{localError || error}</SuccessMsg>
                  ) : null}
                </Form>
                <Divider>Already have an account?</Divider>
                <AuthLink to={pathWithRegion('/login', region)}>Log in</AuthLink>
              </>
            )}
          </AuthCard>
        </Container>
      </AuthWrap>
    </PageLayout>
  );
}

